import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../App';

// ── Static styles (outside component — not recreated on every render) ─────────

const labelStyle = { fontSize: '13px', color: '#AAB0B8', display: 'block' };

function inputStyle(hasError) {
  return {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: `0.5px solid ${hasError ? '#FF766B' : '#3D4349'}`,
    background: '#22272D', color: '#F4FAFF', fontSize: '13px',
    fontFamily: 'Manrope, sans-serif', outline: 'none',
    marginTop: '6px', boxSizing: 'border-box',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

function Login({ onLogin }) {
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isDisabled = loading || !email.trim() || !password;

  const handleLogin = useCallback(async () => {
    if (isDisabled) return;
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );
      if (res.data.success) onLogin(res.data.user);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      // Always runs — prevents button being stuck in "Signing in..." state
      setLoading(false);
    }
  }, [isDisabled, email, password, onLogin]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleLogin();
  }, [handleLogin]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: '100vh', background: '#22272D',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: '40px 20px',
    }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: '12px', marginBottom: '8px',
        }}>
          <img src="/Xicon.png" alt="X Meeting" style={{ width: '40px' }} />
          <div style={{
            fontFamily: 'Bebas Neue, sans-serif', fontSize: '40px',
            fontWeight: '400', color: '#F4FAFF', letterSpacing: '2.0px',
          }}>
            MEETING
          </div>
        </div>
        <div style={{ fontSize: '13px', color: '#AAB0B8', marginTop: '4px' }}>
          Sign in to access meeting documents
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: '#2D3339', border: '0.5px solid #3D4349',
        borderRadius: '16px', padding: '36px', width: '100%', maxWidth: '400px',
      }}>

        {/* Email */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Email</label>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); }}
            onKeyDown={handleKeyDown}
            style={inputStyle(false)}
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              style={{ ...inputStyle(false), paddingRight: '48px' }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', background: 'none',
                border: 'none', cursor: 'pointer',
                color: '#AAB0B8', fontSize: '12px', fontFamily: 'Manrope, sans-serif',
              }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
            background: 'rgba(255,118,107,0.15)', color: '#FF766B', fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          type="button"
          onClick={handleLogin}
          disabled={isDisabled}
          style={{
            width: '100%', padding: '11px', borderRadius: '8px',
            background: '#F1FF5C', color: '#22272D', border: 'none',
            fontSize: '14px', fontWeight: '600', fontFamily: 'Manrope, sans-serif',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.7 : 1,
            transition: 'opacity 0.2s ease',
          }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '32px', display: 'flex', flexDirection: 'column',
        alignItems: 'center', gap: '8px',
      }}>
        <img src="/WEBXPAY_Master_logo-03.png" alt="WEBXPAY" style={{ height: '15px' }} />
        <div style={{ fontSize: '12px', color: '#AAB0B8' }}>
          Powered by WEBXPAY (PVT) LTD
        </div>
      </div>
    </div>
  );
}

export default Login;